import React, { useState, useEffect } from 'react';
import { RootState, useAppSelector } from '../../../../../state/state.tsx';
import { Timestamp } from 'firebase/firestore';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../../../../Config/firebase';
import { toast } from "react-toastify";
import { selectEffectiveUID } from '../../../../../state/UserStatesV2/userSliceV2.ts';
import AttendanceDurationDialog from '../../../../../app/Common/Components/AttendanceDurationDialog.tsx';
import Tooltip from '@mui/material/Tooltip';
import { Slot } from '../../../types/SubscriptionType';
import axios from 'axios';
import { SECRETS_ENV } from "../../../../../Config/firebase.ts";

interface AttendanceUpdate {
    date?: string;
    present?: boolean;
    timestamp?: number;
}

interface AttendanceCalendarProps {
    userId: string;
    livesubId: string;
    subscriptionId: string;
    userName: string;
    subscriptionDays: boolean[];
    isCountHourly: boolean;
    slots?: Slot[];
    slotAssignments?: { [key: string]: any[] };
    refreshKey?: number;
    attendanceUpdate?: AttendanceUpdate;
}

// Update the AttendanceLog type to include new fields
type AttendanceLog = {
    comments: string;
    day: number;
    duration: number;
    logTime: string;  // Store as ISO string for consistency
    updatedBy: string;
    updaterId: string;
    status: 'present' | 'absent';
    slotName?: string;
    isMarkUp?: boolean;
};

interface AttendanceData {
    date: string;
    status: 'present' | 'absent';
    present?: boolean; // Add this field to match the API response
    duration?: number;
    logs?: AttendanceLog[];
    slotName?: string;
    isMarkUp?: boolean;
}

const AttendanceCalendar: React.FC<AttendanceCalendarProps> = ({
    userId,
    livesubId,
    subscriptionId,
    userName,
    subscriptionDays,
    isCountHourly,
    slots = [],
    slotAssignments,
    refreshKey,
    attendanceUpdate,
}) => {
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [attendanceData, setAttendanceData] = useState<{ [key: string]: AttendanceData }>({});
    const [loading, setLoading] = useState(true);
    const [showDurationDialog, setShowDurationDialog] = useState(false);
    const [selectedDay, setSelectedDay] = useState<number | null>(null);
    const [updatingDates, setUpdatingDates] = useState<{ [key: string]: boolean }>({});
    const [showConfirmation, setShowConfirmation] = useState(false);
    const displayName = useAppSelector((state: RootState) => state.userSliceV2.data)?.display_name;
    const uid = useAppSelector((state: RootState) => state.userSliceV2.data)?.uid;
    const providerId = useAppSelector(selectEffectiveUID);

    // Function to get the user's assigned slot
    const getUserAssignedSlot = () => {
        if (!slotAssignments) return '';
        
        for (const [slotId, assignments] of Object.entries(slotAssignments)) {
            const assignment = assignments.find((assignment: any) => assignment.studentId === userId);
            if (assignment) {
                const slot = slots.find(s => s.id === slotId);
                return slot?.name || '';
            }
        }
        return '';
    };

    // Function to get all assigned slots for the user
    const getUserAssignedSlots = () => {
        if (!slotAssignments || !slots.length) return [];
        
        const userSlots: Slot[] = [];
        for (const [slotId, assignments] of Object.entries(slotAssignments)) {
            const assignment = assignments.find((assignment: any) => assignment.studentId === userId);
            if (assignment) {
                const slot = slots.find(s => s.id === slotId);
                if (slot) {
                    userSlots.push(slot);
                }
            }
        }
        return userSlots;
    };

    // Function to check if a day is scheduled based on user's assigned slots or subscription days
    const isScheduledDay = (date: Date) => {
        const userSlots = getUserAssignedSlots();
        const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
        
        // If user has assigned slots, check if the date falls on any of those specific slot days
        if (userSlots.length > 0) {
            const isSlotDay = userSlots.some(slot => {
                const slotDays = slot.dayOfWeek || [];
                // Case-insensitive comparison with full day names
                return slotDays.some(slotDay => 
                    slotDay.toLowerCase() === dayName.toLowerCase()
                );
            });
            return isSlotDay;
        }
        
        // If no slots assigned, use subscription days
        const dayIndex = date.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
        // Convert to subscription days array index: [Mon, Tue, Wed, Thu, Fri, Sat, Sun]
        // So: Sunday(0) -> 6, Monday(1) -> 0, Tuesday(2) -> 1, etc.
        const subscriptionIndex = dayIndex === 0 ? 6 : dayIndex - 1;
        const isSubscriptionDay = subscriptionDays[subscriptionIndex];
        
        return isSubscriptionDay;
    };

    // Function to format log time
    const formatLogTime = (logTime: any): string => {

        console.log("logTime", typeof logTime, logTime)
        if (typeof logTime === 'string') {
            return new Date(logTime).toLocaleTimeString([], { 
                hour: '2-digit', 
                minute: '2-digit' 
            });
        }
        if (logTime instanceof Timestamp) {
            console.log("it is a timestamp")
            return logTime.toDate().toLocaleTimeString([], { 
                hour: '2-digit', 
                minute: '2-digit' 
            });
        }
        // Handle Firebase Timestamp object format {_seconds, _nanoseconds}
        if (logTime && typeof logTime === 'object' && '_seconds' in logTime && '_nanoseconds' in logTime) {
            console.log("it is a timestamp object with _seconds and _nanoseconds")
            const date = new Date(logTime._seconds * 1000 + logTime._nanoseconds / 1000000);
            return date.toLocaleTimeString([], { 
                hour: '2-digit', 
                minute: '2-digit' 
            });
        }
        console.log("it is a date or fallback")
        return new Date().toLocaleTimeString([], { 
            hour: '2-digit', 
            minute: '2-digit' 
        });
    };

    const handleMonthChange = (increment: boolean) => {
        const newDate = new Date(selectedDate);
        const today = new Date();

        if (increment) {
            // Prevent moving beyond current month
            const nextMonth = new Date(newDate);
            nextMonth.setMonth(newDate.getMonth() + 1);
            if (nextMonth > today) {
                return;
            }
            newDate.setMonth(newDate.getMonth() + 1);
        } else {
            newDate.setMonth(newDate.getMonth() - 1);
        }
        setSelectedDate(newDate);
    };

    // Function to fetch attendance data
    const fetchAttendance = async () => {
        try {
            setLoading(true);
            const response = await axios.post(SECRETS_ENV.API_END_POINT + '/api/fetchattendance', {
                userId,
                livesubId,
                subscriptionId,
                providerId,
                year: selectedDate.getFullYear(),
                month: selectedDate.getMonth() + 1,
                isCountHourly,
            });
            
            // Map logs and duration for isCountHourly
            const mappedAttendance: { [key: string]: AttendanceData } = {};
            
            response.data.attendance.forEach((item: any) => {
                const date = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(item.day).padStart(2, '0')}`;
                
                mappedAttendance[date] = {
                    date: date,
                    status: item.present ? 'present' : 'absent',
                    present: item.present, // Include the present field from API
                    duration: item.duration,
                    isMarkUp: Boolean(item.isMarkUp), // Use direct field from API
                    logs: item.logs?.map((log: any) => ({
                        ...log,
                        logTime: log.logTime?.toDate?.() ? log.logTime.toDate().toISOString() : log.logTime,
                        isMarkUp: Boolean(log.isMarkUp) // Use only isMarkUp
                    })) || []
                };
            });
            
            setAttendanceData(mappedAttendance);
        } catch (error) {
            console.error('Error fetching attendance:', error);
            toast.error('Failed to fetch attendance data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAttendance();
    }, [selectedDate, userId, livesubId, subscriptionId, providerId, isCountHourly, refreshKey]);

    // Effect to handle attendance updates
    useEffect(() => {
        if (attendanceUpdate?.date && attendanceUpdate?.present !== undefined) {
            setAttendanceData(prevData => {
                const newData = { ...prevData };
                const dateStr = attendanceUpdate.date || '';
                
                if (newData[dateStr]) {
                    newData[dateStr] = {
                        ...newData[dateStr],
                        status: attendanceUpdate.present ? 'present' : 'absent',
                        logs: [
                            ...(newData[dateStr].logs || []),
                            {
                                comments: '',
                                day: parseInt(dateStr.split('-')[2]),
                                duration: 0,
                                logTime: new Date(attendanceUpdate.timestamp || Date.now()).toISOString(),
                                updatedBy: userName,
                                updaterId: userId,
                                status: attendanceUpdate.present ? 'present' : 'absent'
                            }
                        ]
                    };
                } else {
                    newData[dateStr] = {
                        date: dateStr,
                        status: attendanceUpdate.present ? 'present' : 'absent',
                        duration: 0,
                        logs: [{
                            comments: '',
                            day: parseInt(dateStr.split('-')[2]),
                            duration: 0,
                            logTime: new Date(attendanceUpdate.timestamp || Date.now()).toISOString(),
                            updatedBy: userName,
                            updaterId: userId,
                            status: attendanceUpdate.present ? 'present' : 'absent'
                        }]
                    };
                }
                return newData;
            });
        }
    }, [attendanceUpdate, userName, userId]);

    // Update the setupLivesubListener function to handle logTime correctly
    const setupLivesubListener = () => {
        if (!providerId || !subscriptionId || !livesubId) {
            return undefined;
        }

        try {
            const docRef = doc(db, `ServiceProviders/${providerId}/Subscriptions/${subscriptionId}/LiveSubscriptions/${livesubId}`);
            
            const unsubscribe = onSnapshot(docRef, (docSnapshot) => {
                const data = docSnapshot.data();
                if (data?.attendance) {
                    // Convert any Timestamp to ISO string
                    const processedAttendance = data.attendance.map((item: any) => {
                        const logTime = item.logTime;
                        const timestamp = logTime instanceof Timestamp ? logTime.toDate() : new Date();
                        return {
                            ...item,
                            logTime: timestamp.toISOString()
                        };
                    });
                    
                    // Update local state
                    setAttendanceData(prev => ({
                        ...prev,
                        [data.date]: {
                            ...data,
                            attendance: processedAttendance
                        }
                    }));
                }
            });

            return () => {
                if (typeof unsubscribe === 'function') {
                    unsubscribe();
                }
            };
        } catch (error) {
            console.error('Error setting up attendance listener:', error);
            return undefined;
        }
    };

    // Effect to setup listener
    useEffect(() => {
        const cleanup = setupLivesubListener();
        return () => {
            if (cleanup) {
                cleanup();
            }
        };
    }, [providerId, subscriptionId, livesubId]);

    const getDaysInMonth = (date: Date) => {
        return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    };

    
    const getFirstDayOfMonth = (date: Date) => {
        return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    };

    const getAttendanceStatus = (day: number) => {
        const dateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        const attendance = attendanceData[dateStr];
        const dayDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), day);
        const isScheduledDayValue = isScheduledDay(dayDate);

        let status;
        
        if (attendance) {
            // If attendance record exists, check the present field
            if (attendance.present === true) {
                // Student was marked as present - show on any day
                if (attendance.isMarkUp === true) {
                    status = 'makeup';
                } else {
                    status = 'present';
                }
            } else if (attendance.present === false && isScheduledDayValue) {
                // Student was marked as absent - only show on scheduled days
                status = 'absent';
            } else {
                // present is false but not a scheduled day, or present is undefined
                status = undefined;
            }
        } else {
            // No attendance record exists - only show absent on scheduled days that are in the past
            if (isScheduledDayValue && dayDate <= new Date()) {
                status = 'absent';
            } else {
                status = undefined;
            }
        }

        return status;
    };

    // Get count of makeup classes for the current month
    const getMakeupClassCount = () => {
        let count = 0;
        Object.entries(attendanceData).forEach(([dateStr, data]) => {
            // Only count if the date is in the current selected month
            const [year, month] = dateStr.split('-').map(Number);
            if (year === selectedDate.getFullYear() && month === selectedDate.getMonth() + 1) {
                // Count days that are marked as makeup classes using the direct field
                if (data.isMarkUp === true) {
                    count++;
                }
            }
        });
        return count;
    };

    // Get count of present days for the current month (including makeup classes)
    const getPresentDaysCount = () => {
        let count = 0;
        Object.entries(attendanceData).forEach(([dateStr, data]) => {
            // Only count if the date is in the current selected month
            const [year, month] = dateStr.split('-').map(Number);
            if (year === selectedDate.getFullYear() && month === selectedDate.getMonth() + 1) {
                if (data.status === 'present') {
                    count++;
                }
            }
        });
        return count;
    };

    const handleDayClick = async (day: number) => {
        setSelectedDay(day);
        setShowDurationDialog(true);
    };

    // Handle confirm attendance
    const handleConfirmAttendance = async (durationValue: number, commentsValue: string, slotName?: string, isMarkUp?: boolean, present?: boolean) => {
        if (!selectedDay || !providerId || !uid || !displayName) return;

        const dateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`;
        setUpdatingDates(prev => ({ ...prev, [dateStr]: true }));

        try {
            if (isCountHourly) {
                const payload = {
                    providerId,
                    liveSubscriptionId: livesubId,
                    subscriptionId,
                    year: selectedDate.getFullYear(),
                    month: selectedDate.getMonth() + 1,
                    day: selectedDay,
                    comments: commentsValue,
                    duration: durationValue,
                    logTime: new Date().toISOString(),
                    updatedBy: displayName,
                    updaterId: uid,
                    isCountHourly: true,
                    present: true,
                    slotName: slotName,
                    isMarkUp: isMarkUp
                };
                await axios.post(SECRETS_ENV.API_END_POINT + '/api/addattendance', payload);
                
                // Update local state immediately
                const newLog: AttendanceLog = {
                    comments: commentsValue,
                    day: selectedDay,
                    duration: durationValue,
                    logTime: new Date().toISOString(),
                    updatedBy: displayName,
                    updaterId: uid,
                    status: 'present',
                    slotName: slotName,
                    isMarkUp: isMarkUp
                };
                
                setAttendanceData(prev => ({
                    ...prev,
                    [dateStr]: {
                        date: dateStr,
                        status: 'present',
                        present: true, // Add this field
                        duration: durationValue,
                        isMarkUp: isMarkUp,
                        logs: [...(prev[dateStr]?.logs || []), newLog]
                    }
                }));
            } else {
                // Non-hourly: use explicit present/absent selection
                const newStatus = present ? 'present' : 'absent';
                
                const payload = {
                    providerId,
                    liveSubscriptionId: livesubId,
                    subscriptionId,
                    year: selectedDate.getFullYear(),
                    month: selectedDate.getMonth() + 1,
                    day: selectedDay,
                    present: present,
                    isCountHourly: false,
                    duration: 1,
                    logTime: new Date().toISOString(),
                    updatedBy: displayName,
                    updaterId: uid,
                    comments: commentsValue,
                    slotName: slotName,
                    isMarkUp: isMarkUp
                };
                await axios.post(SECRETS_ENV.API_END_POINT + '/api/addattendance', payload);
                
                // Update local state immediately
                const newLog: AttendanceLog = {
                    comments: commentsValue,
                    day: selectedDay,
                    duration: 1,
                    logTime: new Date().toISOString(),
                    updatedBy: displayName,
                    updaterId: uid,
                    status: newStatus,
                    slotName: slotName,
                    isMarkUp: isMarkUp
                };
                
                setAttendanceData(prev => ({
                    ...prev,
                    [dateStr]: {
                        date: dateStr,
                        status: newStatus as 'present' | 'absent',
                        present: present, // Add this field
                        duration: 1,
                        isMarkUp: Boolean(isMarkUp),
                        logs: [...(prev[dateStr]?.logs || []), newLog]
                    }
                }));
            }
        } catch (error) {
            console.error('Error marking attendance:', error);
            toast.error('Failed to mark attendance');
        } finally {
            setUpdatingDates(prev => ({ ...prev, [dateStr]: false }));
            setShowDurationDialog(false);
            setSelectedDay(null);
        }
    };

    if (loading) {
        return <div className="flex justify-center items-center h-48">Loading...</div>;
    }

    const daysInMonth = getDaysInMonth(selectedDate);
    const firstDay = getFirstDayOfMonth(selectedDate);
    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    // Render the calendar day cell
    const renderDayCell = (day: number, logs: AttendanceLog[] | undefined, status: string | undefined, dayDate: Date, isFutureDate: boolean, isUpdating: boolean) => {
        // Only create tooltip content if there are logs
        const hasLogs = logs && logs.length > 0;
        
        const tooltipContent = hasLogs ? (
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-3 max-w-xs">
                {logs.map((log, idx) => (
                    <div key={idx} className="mb-3 last:mb-0 border-b last:border-0 border-gray-100 pb-2 last:pb-0">
                        <div className="flex items-center gap-2 mb-1">
                            <span className={`font-medium ${
                                log.status === 'present' 
                                    ? (log.isMarkUp === true)
                                        ? 'text-purple-700' 
                                        : 'text-green-700'
                                    : 'text-red-700'
                            }`}>
                                {log.status === 'present' ? ((log.isMarkUp === true) ? 'Makeup' : 'Present') : 'Absent'}
                            </span>
                            <span className="text-gray-400">•</span>
                            <span className="text-gray-600">{formatLogTime(log.logTime)}</span>
                            {log.duration && isCountHourly && (
                                <>
                                    <span className="text-gray-400">•</span>
                                    <span className="text-green-600 font-medium">{log.duration}h</span>
                                </>
                            )}
                        </div>
                        {(log.isMarkUp === true) && (
                            <div className="flex items-center gap-1 text-sm text-purple-700 bg-purple-50 px-2 py-0.5 rounded mb-1 w-fit">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                                </svg>
                                <span>Makeup Class</span>
                            </div>
                        )}
                        {log.slotName && (
                            <div className="flex items-center gap-1 text-sm text-blue-700 bg-blue-50 px-2 py-0.5 rounded mb-1 w-fit">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                </svg>
                                <span>{log.slotName}</span>
                            </div>
                        )}
                        {log.comments && log.comments.trim() && (
                            <div className="text-sm text-gray-600 bg-gray-50 px-2 py-1 rounded mt-1">
                                {log.comments}
                            </div>
                        )}
                        <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                            </svg>
                            {log.updatedBy}
                        </div>
                    </div>
                ))}
            </div>
        ) : null;

        return (
            <Tooltip
                title={tooltipContent}
                arrow
                placement="top"
                disableHoverListener={!hasLogs}
                componentsProps={{
                    tooltip: {
                        sx: {
                            bgcolor: 'transparent',
                            '& .MuiTooltip-arrow': {
                                color: 'white'
                            }
                        }
                    }
                }}
            >
                <div
                    className={`h-8 flex items-center justify-center rounded-full text-xs relative
                        ${isFutureDate ? "cursor-not-allowed bg-gray-100 text-gray-400" :
                            status === 'makeup'
                                ? "bg-purple-100 text-purple-800 cursor-pointer hover:bg-purple-200"
                                : status === 'present' 
                                    ? "bg-green-100 text-green-800 cursor-pointer hover:bg-green-200"
                                    : status === 'absent' 
                                        ? "bg-red-100 text-red-800 cursor-pointer hover:bg-red-200"
                                        : "bg-white border border-gray-300 cursor-pointer hover:bg-gray-100"
                        }
                    `}
                    onClick={() => !isUpdating && !isFutureDate && handleDayClick(day)}
                >
                    {day}
                    {/* Show indicators for slot */}
                    {(status === 'present' || status === 'makeup') && logs && logs.some(log => log.slotName) && (
                        <div className="absolute -top-1 -right-1">
                            <div className="w-2 h-2 bg-blue-500 rounded-full shadow-sm" title="Slot Assigned"/>
                        </div>
                    )}
                </div>
            </Tooltip>
        );
    };

    return (
        <div className="border-[1px] border-gray-300 bg-white shadow-lg p-3 rounded-lg relative">
            <div className="text-base font-medium text-center mb-3">
                <p>
                    <span className="text-gray-700">{userName} present for </span>
                    <span className="text-xl font-bold text-green-600">{getPresentDaysCount()} days</span>
                    {getMakeupClassCount() > 0 && (
                        <span>
                            <span className="text-gray-700"> (including </span>
                            <span className="font-bold text-purple-600">{getMakeupClassCount()} make-up</span>
                            <span className="text-gray-700"> {getMakeupClassCount() === 1 ? 'class' : 'classes'}</span>
                            <span className="text-gray-700">)</span>
                        </span>
                    )}
                    <span className="font-semibold"> in {monthNames[selectedDate.getMonth()]} {selectedDate.getFullYear()}</span>
                </p>
                <p className="text-xs text-gray-500 mt-1">
                    💡 Hover over dates to see detailed logs. Students may have multiple attendance records per day.
                </p>
            </div>

            {/* Legend */}
            <div className="flex justify-center items-center gap-4 mb-4 text-xs">
                <div className="flex items-center gap-1">
                    <div className="w-4 h-4 rounded-full bg-green-100 border border-green-200"></div>
                    <span>Present</span>
                </div>
                <div className="flex items-center gap-1">
                    <div className="w-4 h-4 rounded-full bg-purple-100 border border-purple-200"></div>
                    <span>Makeup</span>
                </div>
                <div className="flex items-center gap-1">
                    <div className="w-4 h-4 rounded-full bg-red-100 border border-red-200"></div>
                    <span>Absent</span>
                </div>
                <div className="flex items-center gap-1">
                    <div className="w-4 h-4 rounded-full bg-white border border-gray-300"></div>
                    <span>Scheduled</span>
                </div>
            </div>

            <div className="flex justify-between items-center mb-3">
                <button
                    onClick={() => handleMonthChange(false)}
                    className="p-1 hover:bg-gray-200 rounded text-sm"
                >
                    ←
                </button>
                <div className="text-base font-semibold">
                    {monthNames[selectedDate.getMonth()]} {selectedDate.getFullYear()}
                </div>
                <button
                    onClick={() => handleMonthChange(true)}
                    className="p-1 hover:bg-gray-200 rounded text-sm"
                >
                    →
                </button>
            </div>
            <div className="grid grid-cols-7 gap-1">
                {weekDays.map(day => (
                    <div key={day} className="text-center text-xs font-medium py-1">
                        {day}
                    </div>
                ))}
                {Array.from({ length: firstDay }).map((_, index) => (
                    <div key={`empty-${index}`} className="h-8" />
                ))}
                {Array.from({ length: daysInMonth }).map((_, index) => {
                    const day = index + 1;
                    const dayDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), day);
                    const status = getAttendanceStatus(day);
                    const dateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                    const isUpdating = updatingDates[dateStr];
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    dayDate.setHours(0, 0, 0, 0);
                    const isFutureDate = dayDate > today;
                    // Find logs for this day
                    const attendance = attendanceData[dateStr];
                    const logs = attendance?.logs || [];

                    return renderDayCell(day, logs, status, dayDate, isFutureDate, isUpdating);
                })}
            </div>

            {/* Confirmation Dialog */}
            {showConfirmation && selectedDate && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg shadow-xl max-w-sm w-full mx-4">
                        <h3 className="text-lg font-semibold mb-4">Confirm Attendance Change</h3>
                        <p className="mb-6">
                            {isCountHourly ? (
                                `Do you want to mark attendance for ${userName} on ${monthNames[selectedDate.getMonth()]} ${selectedDate.getDate()}, ${selectedDate.getFullYear()}?`
                            ) : (
                                `Are you sure you want to mark ${userName} as ${selectedDate.getDate() === selectedDate.getMonth() + 1 ? 'absent' : 'present'} for ${monthNames[selectedDate.getMonth()]} ${selectedDate.getDate()}, ${selectedDate.getFullYear()}?`
                            )}
                        </p>
                        <div className="flex justify-end space-x-4">
                            <button
                                onClick={() => setShowConfirmation(false)}
                                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    setShowConfirmation(false);
                                    setSelectedDate(new Date()); // Reset selectedDate to current month
                                }}
                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Use shared AttendanceDurationDialog for isCountHourly */}
            {showDurationDialog && selectedDay !== null && (() => {
                const dateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`;
                const currentStatus = !isCountHourly ? attendanceData[dateStr]?.status : undefined;
                const initialMakeupStatus = attendanceData[dateStr]?.isMarkUp;
                
                return (
                    <AttendanceDurationDialog
                        open={showDurationDialog}
                        onClose={() => {
                            setShowDurationDialog(false);
                            setSelectedDay(null);
                        }}
                        onSubmit={handleConfirmAttendance}
                        initialDuration={1}
                        hasSlots={slots && slots.length > 0}
                        slots={slots}
                        isCountHourly={isCountHourly}
                        initialSlot={getUserAssignedSlot()}
                        loading={Object.values(updatingDates).some(Boolean)}
                        currentStatus={currentStatus}
                        initialMakeupStatus={initialMakeupStatus}
                        userName={userName}
                        selectedDay={String(selectedDay)}
                    />
                );
            })()}
        </div>
    );
};

export default AttendanceCalendar;